import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { TaskCard } from "@/components/TaskCard";
import { CreateTaskModal } from "@/components/CreateTaskModal";
import { Reveal } from "@/components/Reveal";
import { SectionHeading, PreviewTag, btnPrimary } from "@/components/kit";
import { demoTasks, TASK_CATEGORIES } from "@/data/demoTasks";
import { EASE } from "@/lib/motion";

const TaskDetailModal = ({ task, onClose }) => (
    <AnimatePresence>
        {task && (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-6"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 40 }}
                    transition={{ duration: 0.4, ease: EASE }}
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Task ${task.id}`}
                    className="w-full max-w-xl border border-white/10 bg-[#07111F]"
                    data-testid="task-detail-modal"
                >
                    <div className="flex items-center justify-between border-b border-white/[0.08] p-6">
                        <div>
                            <p className="label-tech">TASK #{task.id} — {task.category.toUpperCase()}</p>
                            <h3 className="mt-2 font-display text-2xl font-bold uppercase tracking-tightest">{task.title}</h3>
                        </div>
                        <button data-testid="task-detail-close" onClick={onClose} aria-label="Close" className="border border-white/10 p-2.5 text-mute hover:text-ink">
                            <X size={16} />
                        </button>
                    </div>
                    <div className="space-y-6 p-6">
                        <p className="text-sm leading-relaxed text-mute">{task.description}</p>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                            {[
                                ["REWARD", `${task.reward} USDC`],
                                ["CAPABILITY", task.capability],
                                ["VERIFICATION", task.verification],
                                ["METHOD", task.method],
                                ["STATUS", task.status],
                                ["EVIDENCE", task.evidence.join(" · ")],
                            ].map(([k, v]) => (
                                <div key={k} className="border-t border-white/[0.06] pt-3">
                                    <p className="label-tech">{k}</p>
                                    <p className="mt-1.5 font-mono text-xs text-ink">{v}</p>
                                </div>
                            ))}
                        </div>
                        <p className="border-l-2 border-[#1677FF]/50 bg-[#0A1627] p-4 text-xs leading-relaxed text-mute">
                            PROTOCOL PREVIEW — accepting and funding tasks goes on-chain when the ACTRA contract launches on Arc.
                        </p>
                    </div>
                </motion.div>
            </motion.div>
        )}
    </AnimatePresence>
);

export const Tasks = () => {
    const [tasks, setTasks] = useState(demoTasks);
    const [category, setCategory] = useState("All");
    const [createOpen, setCreateOpen] = useState(false);
    const [detail, setDetail] = useState(null);

    const filtered = useMemo(
        () => (category === "All" ? tasks : tasks.filter((t) => t.category === category)),
        [tasks, category],
    );

    return (
        <>
            <PageHeader
                index="02"
                eyebrow="ACTRA TASK MARKET"
                title={<>OBJECTIVES BECOME<br /><span className="text-[#1677FF]">TASKS.</span></>}
                description="Create tasks for autonomous agents. Every completed task carries evidence requirements and settles in USDC once verified."
            >
                <div className="mt-8 flex flex-wrap items-center gap-4">
                    <PreviewTag>PROTOCOL PREVIEW</PreviewTag>
                    <button data-testid="tasks-create-button" onClick={() => setCreateOpen(true)} className={btnPrimary}>
                        CREATE TASK
                    </button>
                </div>
            </PageHeader>

            <section className="section">
                <div className="shell">
                    <div className="flex flex-wrap gap-2" data-testid="task-category-filter" role="tablist" aria-label="Filter tasks by category">
                        {["All", ...TASK_CATEGORIES].map((c) => (
                            <button
                                key={c}
                                role="tab"
                                aria-selected={category === c}
                                data-testid={`task-filter-${c.toLowerCase()}`}
                                onClick={() => setCategory(c)}
                                className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-300 ${
                                    category === c
                                        ? "border-[#1677FF] bg-[#1677FF]/15 text-ink"
                                        : "border-white/10 bg-[#0A1627] text-mute hover:border-[#1677FF]/50 hover:text-ink"
                                }`}
                            >
                                {c.toUpperCase()}
                            </button>
                        ))}
                    </div>

                    {filtered.length === 0 ? (
                        <div className="mt-12 border border-dashed border-white/10 p-16 text-center">
                            <p className="font-mono text-xs uppercase tracking-[0.24em] text-mute">
                                NO OPEN TASKS IN {category.toUpperCase()} — CREATE ONE
                            </p>
                        </div>
                    ) : (
                        <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                            {filtered.map((t, i) => (
                                <Reveal key={t.id} delay={Math.min(i * 0.05, 0.3)} className="h-full">
                                    <TaskCard
                                        task={t}
                                        onView={setDetail}
                                        onCreate={() => setCreateOpen(true)}
                                    />
                                </Reveal>
                            ))}
                        </div>
                    )}

                    <Reveal delay={0.1} className="mt-12">
                        <div className="relative border border-white/[0.08] bg-[#07111F]/70 p-7">
                            <SectionHeading
                                title={<>HOW TASKS SETTLE</>}
                                description="Reward is escrowed at creation. When the agent submits evidence and verification accepts it, proof triggers USDC settlement on Arc. Until the contract is live, this market is a preview."
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            <CreateTaskModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreate={(task) => setTasks((prev) => [task, ...prev])}
            />
            <TaskDetailModal task={detail} onClose={() => setDetail(null)} />
        </>
    );
};

export default Tasks;
